var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Tooltip_1;
import { defaultOptions } from '@slickdeals/durango/dist/javascript/decorators/defaultOptions';
import { stateable } from '@slickdeals/durango/dist/javascript/decorators/stateable';
import { injectable } from '@slickdeals/durango/dist/javascript/decorators/injectable';
import { classNameWithSubClass, classNameWithModifier } from '@slickdeals/durango/dist/javascript/utilities/css';
import { getFilteredDataAttributeValues } from '@slickdeals/durango/dist/javascript/utilities/dom';
import Logger from '@slickdeals/durango/dist/javascript/utilities/logger';
import { includesSomeKeys } from '@slickdeals/durango/dist/javascript/utilities/object';
import { isHtmlElement } from '@slickdeals/durango/dist/javascript/utilities/guards';
import { makePositioned, positionElements, Positions, } from '@slickdeals/durango/dist/javascript/utilities/positioning';
import Icon from './icon';
import BlueprintComponent from '../core/blueprintComponent';
import '../../scss/components/tooltip.scss';
/** The name of the module */
export const name = 'Tooltip';
/** The CSS classnames used by this module */
export var ClassNames;
(function (ClassNames) {
    ClassNames["Default"] = "bp-c-tooltip";
    ClassNames["Orbiting"] = "bp-c-tooltip--orbiting";
    ClassNames["JavaScript"] = "js-tooltip";
})(ClassNames || (ClassNames = {}));
/** The delay, in milliseconds, before the tooltip disappears once the cursor leaves the trigger element */
const HIDE_DELAY = 250;
/** Size modifiers for sizing of the tooltip element */
export var TooltipSize;
(function (TooltipSize) {
    TooltipSize["ExtraSmall"] = "xsmall";
    TooltipSize["Small"] = "small";
    TooltipSize["Default"] = "";
    TooltipSize["Large"] = "large";
})(TooltipSize || (TooltipSize = {}));
// Temporary until this module extends Popup and the Popup positioning can check if a positioned element goes off-screen
export var TooltipPosition;
(function (TooltipPosition) {
    TooltipPosition["BottomLeft"] = "bottomLeft";
    TooltipPosition["BottomCenter"] = "bottomCenter";
    TooltipPosition["BottomRight"] = "bottomRight";
    TooltipPosition["TopLeft"] = "topLeft";
    TooltipPosition["TopCenter"] = "topCenter";
    TooltipPosition["TopRight"] = "topRight";
})(TooltipPosition || (TooltipPosition = {}));
// This is the default max width of the tooltip
const defaultMaxWidth = 400;
const minWidthOverride = 0;
/** The default options set for this module */
export const DefaultOptions = {
    size: TooltipSize.Default,
    showOnInit: false,
};
/** Type guard for testing if provided argument is an Options object */
const isOptions = (options) => typeof options === 'object' && includesSomeKeys(options, ['triggerElement', 'triggerId', 'size', 'contents']);
let Tooltip = Tooltip_1 = class Tooltip extends BlueprintComponent {
    /**
     *
     * @param triggerElementOrOptions The element that will trigger the tooltip to appear, or options to initialize the component with
     * @param options Options to initialize the component with
     */
    constructor(triggerElementOrOptions, options = DefaultOptions) {
        super(name, options);
        // Test to see if the first argument is the triggerElement or the options object
        if (isHtmlElement(triggerElementOrOptions)) {
            this.triggerElement = triggerElementOrOptions;
        }
        else if (isOptions(triggerElementOrOptions)) {
            this.options = triggerElementOrOptions;
            // Check if the actual triggerElement was passed in, or if it's an ID that requires lookup
            if (isHtmlElement(triggerElementOrOptions.triggerElement)) {
                this.triggerElement = triggerElementOrOptions.triggerElement;
            }
            else if (typeof triggerElementOrOptions.triggerId === 'string') {
                this.triggerElement = document.getElementById(triggerElementOrOptions.triggerId);
            }
            else {
                Logger.warn('Trigger element not provided for Tooltip initialization');
            }
        }
        // Define the initial state
        const initialState = {
            isVisible: false,
        };
        // Initialize the component state
        this.initState(initialState);
    }
    /**
     * Performs any necessary initialization for the component
     */
    init() {
        super.init();
        // Create the HTML element
        this.element = Tooltip_1.createElement(this.options);
        // Assign the anchor point
        this.anchorTo = this.options.anchorTo || this.triggerElement;
        if (typeof this.anchorTo === 'undefined') {
            throw new Error(`please define an anchor point for the tooltip using trigger: ${this.options.triggerId}`);
        }
        if (typeof this.anchorTo !== 'undefined') {
            // Append the tooltip element to the trigger element
            (this.options.appendTo || this.anchorTo).appendChild(this.element);
            // Since the tooltip element is absolutely positioned, it needs to be in a positioned container
            makePositioned(this.anchorTo);
            // If a trigger element exists, add event listeners for it
            this.anchorTo.addEventListener('mouseover', this.handleShow.bind(this));
            this.anchorTo.addEventListener('mouseout', this.handleHide.bind(this));
        }
        // If we're not appending to the trigger element make it orbiting
        if (this.options.appendTo && this.options.appendTo !== this.anchorTo) {
            this.element.classList.add(ClassNames.Orbiting);
        }
        if (this.options.showOnInit) {
            this.handleShow();
        }
        const closeIcon = Icon.createElement({
            name: 'close',
        });
        closeIcon.addEventListener('click', () => {
            this.hide();
            this.element.classList.add(classNameWithModifier(ClassNames.Default, 'hidden'));
        });
        this.element.prepend(closeIcon);
    }
    destroy(cleanDom) {
        if (cleanDom) {
            this.element.remove();
        }
    }
    /**
     * Absolutely position the tooltip if we have to.
     */
    positionTooltip() {
        // If the tooltip is injected in the anchor we don't need to do any positioning.
        if (!this.options.appendTo || this.options.appendTo === this.anchorTo) {
            return;
        }
        positionElements({
            target: this.anchorTo,
            orbiter: this.element,
            position: this.options.positionAppendTo,
        });
        // This allows us to have responsive tooltips
        // that are aware of the viewport
        const positionDelta = window.innerWidth - this.element.getClientRects()[0].x;
        // The space between the window and the edge
        // of the tooltip
        const edgeGap = 12;
        // This is the default max width of tooltips
        // We do not want to over ride the max width
        // if it's greater than the default
        if (positionDelta < defaultMaxWidth) {
            this.element.style.maxWidth = `${positionDelta - edgeGap}px`;
            this.element.style.minWidth = `${minWidthOverride}px`;
        }
        else {
            // If it's not less than, remove the property
            // so we're not overriding the default
            this.element.style.removeProperty('max-width');
            this.element.style.removeProperty('min-width');
        }
    }
    /**
     * Show the tooltip
     */
    show() {
        this.element.classList.add(classNameWithModifier(ClassNames.Default, 'visible'));
        this.element.classList.remove(classNameWithModifier(ClassNames.Default, 'hidden'));
        this.positionTooltip();
        this.setState({
            isVisible: true,
        });
    }
    /**
     * Hide the tooltip
     */
    hide() {
        this.element.classList.remove(classNameWithModifier(ClassNames.Default, 'visible'));
        this.setState({
            isVisible: false,
        });
    }
    /**
     * Show the tooltip on cursor hover
     */
    handleShow() {
        if (!this.getState().isVisible) {
            this.show();
        }
        window.clearTimeout(this.hideTimeout);
    }
    /**
     * Hide the tooltip on cursor exit
     */
    handleHide() {
        this.hideTimeout = window.setTimeout(this.hide.bind(this), HIDE_DELAY);
    }
    /**
     * Create a Tooltip instance from an already-present DOM element using data attributes
     *
     * @param triggerElement The HTML element that triggers the tooltip to appear
     */
    static createFromDataAttributes(triggerElement) {
        // Fetch any options using a 'data-tooltip' attribute
        const attributes = getFilteredDataAttributeValues(triggerElement, 'tooltip');
        // Create and return the Tooltip instance
        return new Tooltip_1(triggerElement, attributes);
    }
    /**
     * Create a Tooltip HTML element
     *
     * @param options Options to initialize the component with
     */
    static createElement(options = {}) {
        const element = document.createElement('div');
        element.classList.add(ClassNames.Default);
        // Set the size if it's supplied as an option
        if (options.modifiers?.size) {
            element.classList.add(classNameWithModifier(ClassNames.Default, options.modifiers.size));
        }
        // Set position modifiers, or default to bottom left
        if (options.modifiers?.position) {
            element.classList.add(classNameWithModifier(ClassNames.Default, options.modifiers.position));
        }
        else {
            element.classList.add(classNameWithModifier(ClassNames.Default, TooltipPosition.BottomLeft));
        }
        let { contents } = options;
        // Either create text contents or use the supplied contents
        if (typeof contents === 'string') {
            contents = document.createTextNode(contents);
        }
        // Create the content
        const contentElement = document.createElement('div');
        contentElement.classList.add(classNameWithSubClass(ClassNames.Default, 'content'));
        contentElement.appendChild(contents);
        // Finish building the element
        element.appendChild(contentElement);
        return element;
    }
};
Tooltip = Tooltip_1 = __decorate([
    stateable(name),
    injectable(name),
    defaultOptions({
        positionAppendTo: [Positions.Top, Positions.Left],
    })
], Tooltip);
export default Tooltip;
BlueprintComponent.instantiate(name, {
    fromHydrationQueue: true,
    fromDom: true,
    fromMutation: true,
});
